import React, { createContext, useContext, useState, useMemo, ReactNode } from 'react'
import BigNumber from 'bignumber.js'
import { FIXED_STRD_SUPPLY, getNumberInput } from './general'

interface BurnResults {
  yourStrdPercent: number
  yourStrd: number
  yourStrdRPercent: number
  yourStrdR: number
  totalStrdRemaining: number
  totalStrdRMinted: number
  postBurnSupply: number
  wasAdjusted: boolean
  yourStrdPercentDelta: number
  yourStrdRInBips: boolean
  yourStrdInBips: boolean
}

interface BurnContextValue {
  strdAmount: string
  setStrdAmount: (value: string) => void
  burnAmount: string
  setBurnAmount: (value: string) => void
  networkBurnAmount: string
  setNetworkBurnAmount: (value: string) => void
  handleBurnPreset: (percentage: number) => void
  results: BurnResults
}

const BurnContext = createContext<BurnContextValue | undefined>(undefined)

export const useBurn = () => {
  const context = useContext(BurnContext)
  if (!context) {
    throw new Error('useBurn must be used within BurnProvider')
  }
  return context
}

interface BurnProviderProps {
  children: ReactNode
}

export const BurnProvider: React.FC<BurnProviderProps> = ({ children }) => {
  const [strdAmount, setStrdAmount] = useState('')
  const [burnAmount, setBurnAmount] = useState('')
  const [networkBurnAmount, setNetworkBurnAmount] = useState(String(70_000_000))

  const results = useMemo(() => {
    const holdings = new BigNumber(getNumberInput(strdAmount))
    const burn = new BigNumber(getNumberInput(burnAmount))
    const inputNetworkBurn = new BigNumber(getNumberInput(networkBurnAmount))

    // If network burn < your burn, adjust it to at least your burn
    const actualNetworkBurn = BigNumber.max(inputNetworkBurn, burn)
    const wasAdjusted = actualNetworkBurn.isGreaterThan(inputNetworkBurn)

    // Your STRD after burn = holdings - your burn
    const yourStrdPostBurn = holdings.minus(burn)

    // Total STRD remaining = 100M - actual network burn
    const totalStrdRemaining = new BigNumber(FIXED_STRD_SUPPLY).minus(actualNetworkBurn)

    // Your % of STRD = (your STRD after / total STRD remaining) * 100
    const yourStrdPercent = totalStrdRemaining.isZero()
      ? new BigNumber(0)
      : yourStrdPostBurn.dividedBy(totalStrdRemaining).multipliedBy(100)

    // Previous % of STRD before your burn, but under the same network burn context
    // i.e., assume you didn't burn personally but the network burn still happened
    const yourStrdPercentBefore = holdings.isZero()
      ? new BigNumber(0)
      : holdings.dividedBy(FIXED_STRD_SUPPLY).multipliedBy(100)

    // Delta = after - before
    const yourStrdPercentDelta = yourStrdPercent.minus(yourStrdPercentBefore)

    // Total STRD-R minted = actual network burn (1:1 ratio)
    // const totalStrdRMinted = actualNetworkBurn
    const totalStrdRMinted = new BigNumber(100_000_000)

    // Your STRD-R received = your burn (1:1 ratio)
    const yourStrdR = burn

    // Your % of STRD-R = (your STRD-R / total STRD-R) * 100
    const yourStrdRPercent = totalStrdRMinted.isZero()
      ? new BigNumber(0)
      : yourStrdR.dividedBy(totalStrdRMinted).multipliedBy(100)

    // Post-burn supply
    const postBurnSupply = new BigNumber(FIXED_STRD_SUPPLY).minus(actualNetworkBurn)

    return {
      yourStrdPercent: yourStrdPercent.toNumber(),
      yourStrd: Math.round(yourStrdPostBurn.toNumber()),
      yourStrdRPercent: yourStrdRPercent.toNumber(),
      yourStrdR: Math.round(yourStrdR.toNumber()),
      totalStrdRemaining: Math.round(totalStrdRemaining.toNumber()),
      totalStrdRMinted: Math.round(totalStrdRMinted.toNumber()),
      postBurnSupply: Math.round(postBurnSupply.toNumber()),
      wasAdjusted,
      yourStrdPercentDelta: yourStrdPercentDelta.toNumber(),
      // We display STRD-R and STRD in bips (basis points) if they are less than 1%
      // We'll use these to show a basic information about the conversion.
      yourStrdInBips: yourStrdPercent.gt(0) && yourStrdPercent.lt(1),
      yourStrdRInBips: yourStrdRPercent.gt(0) && yourStrdRPercent.lt(1)
    }
  }, [strdAmount, burnAmount, networkBurnAmount])

  // Quick preset buttons for your burn
  const handleBurnPreset = (percentage: number) => {
    const holdings = new BigNumber(getNumberInput(strdAmount))
    const burnAmountValue = holdings.multipliedBy(percentage).dividedBy(100).toFixed(0)
    setBurnAmount(burnAmountValue)
  }

  const value: BurnContextValue = {
    strdAmount,
    burnAmount,
    networkBurnAmount,
    setStrdAmount,
    setBurnAmount,
    setNetworkBurnAmount,
    handleBurnPreset,
    results
  }

  return <BurnContext.Provider value={value}>{children}</BurnContext.Provider>
}
